import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  MapPin, 
  Building, 
  Phone, 
  Mail,
  User,
  Filter,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usStates = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const specialties = [
  'Residential Sales',
  'Commercial Sales',
  'Luxury Properties',
  'First-Time Buyers',
  'Investment Properties',
  'Property Management',
  'Relocation',
  'New Construction'
];

const membershipTypes = [
  { id: 'individual', name: 'Individual Agent' },
  { id: 'brokerage', name: 'Brokerage' },
  { id: 'team', name: 'Team' },
  { id: 'affiliate', name: 'Affiliate Partner' },
];

const sampleMembers = [
  {
    user_id: 'user_1',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@example.com',
    phone: '(555) 123-4567',
    picture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
    brokerage: 'Premier Realty Group',
    state: 'California',
    city: 'Los Angeles',
    specialty: 'Luxury Properties',
    membership_type: 'individual'
  },
  {
    user_id: 'user_2',
    name: 'Michael Chen',
    email: 'michael.chen@example.com',
    phone: '(555) 234-5678',
    picture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
    brokerage: 'Urban Living Realty',
    state: 'New York',
    city: 'New York',
    specialty: 'Commercial Sales',
    membership_type: 'brokerage'
  },
  {
    user_id: 'user_3',
    name: 'Emily Davis',
    email: 'emily.davis@example.com',
    phone: '(555) 345-6789',
    picture: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80',
    brokerage: 'Sunshine Properties',
    state: 'Florida',
    city: 'Miami',
    specialty: 'First-Time Buyers',
    membership_type: 'individual'
  },
  {
    user_id: 'user_4',
    name: 'James Wilson',
    email: 'james.wilson@example.com',
    phone: '(555) 456-7890',
    picture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
    brokerage: 'Mountain View Realty',
    state: 'Colorado',
    city: 'Denver',
    specialty: 'Investment Properties',
    membership_type: 'team'
  },
  {
    user_id: 'user_5',
    name: 'Amanda Rodriguez',
    email: 'amanda.rodriguez@example.com',
    phone: '(555) 567-8901',
    brokerage: 'Capital Real Estate',
    state: 'Texas',
    city: 'Austin',
    specialty: 'Residential Sales',
    membership_type: 'individual'
  },
  {
    user_id: 'user_6',
    name: 'David Kim',
    email: 'david.kim@example.com',
    phone: '(555) 678-9012',
    picture: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80',
    brokerage: 'Pacific Realty',
    state: 'Washington',
    city: 'Seattle',
    specialty: 'New Construction',
    membership_type: 'brokerage'
  },
];

export default function DashboardDirectory() {
  const { isActiveMember } = useAuth();
  const [members, setMembers] = useState(sampleMembers);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    state: '',
    specialty: '',
    membership_type: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: sampleMembers.length,
    pages: 1
  });

  useEffect(() => {
    const fetchMembers = async () => {
      if (!isActiveMember) return;
      
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.state) params.append('state', filters.state);
        if (filters.specialty) params.append('specialty', filters.specialty);
        if (filters.membership_type) params.append('membership_type', filters.membership_type);
        params.append('page', pagination.page.toString());

        const response = await fetch(`${API}/members?${params.toString()}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.members && data.members.length > 0) {
            setMembers(data.members);
            setPagination({
              page: data.page,
              total: data.total,
              pages: data.pages
            });
          }
        }
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [filters, pagination.page, isActiveMember]);

  const clearFilters = () => {
    setFilters({
      search: '',
      state: '',
      specialty: '',
      membership_type: ''
    });
  };

  const hasActiveFilters = filters.state || filters.specialty || filters.membership_type;

  if (!isActiveMember) {
    return (
      <div className="text-center py-16" data-testid="directory-locked">
        <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
          Member Directory
        </h2>
        <p className="text-slate-600 mb-6">
          Access to the member directory requires an active membership.
        </p>
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
          Membership Pending Approval
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-directory">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Member Directory</h1>
        <p className="text-slate-600">Connect with AREA members across the country</p>
      </div>

      {/* Search & Filters */}
      <Card className="border border-slate-200 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, brokerage, or city..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 h-12"
                data-testid="directory-search"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`h-12 ${hasActiveFilters ? 'border-[#3D4F7A] text-[#3D4F7A]' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-2 bg-[#3D4F7A] text-white text-xs">Active</Badge>
              )}
            </Button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select 
                  value={filters.state} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, state: value }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Filter by State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All States</SelectItem>
                    {usStates.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.specialty} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, specialty: value }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Filter by Specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Specialties</SelectItem>
                    {specialties.map((specialty) => (
                      <SelectItem key={specialty} value={specialty}>
                        {specialty}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.membership_type} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, membership_type: value }))}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Filter by Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Types</SelectItem>
                    {membershipTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-4 text-slate-600"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear all filters
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <div className="text-sm text-slate-600 mb-4">
        Showing {members.length} of {pagination.total} members
      </div>

      {/* Members Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No members found matching your criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((member) => (
            <Card 
              key={member.user_id} 
              className="border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={member.picture} alt={member.name} />
                    <AvatarFallback className="bg-[#3D4F7A] text-white text-lg">
                      {member.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{member.name}</h3>
                    {member.specialty && (
                      <Badge variant="secondary" className="mt-1 bg-[#3D4F7A]/10 text-[#3D4F7A] text-xs">
                        {member.specialty}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {member.brokerage && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{member.brokerage}</span>
                    </div>
                  )}
                  {(member.city || member.state) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span>{[member.city, member.state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                  {member.email && (
                    <a 
                      href={`mailto:${member.email}`}
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full">
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    </a>
                  )}
                  {member.phone && (
                    <a 
                      href={`tel:${member.phone}`}
                      className="flex-1"
                    >
                      <Button variant="outline" size="sm" className="w-full">
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
